/**
 * Error Classification Utility
 * Classifies errors based on status codes and error types
 * Extracts user-friendly messages from API error responses
 * Determines if errors are retryable
 */

/**
 * Classifies an error and returns structured error information
 * @param {Error} error - The error object from axios
 * @returns {Object|null} Classified error object with type, message, details, and retryable flag, or null if error should not be displayed
 */
export const classifyError = (error) => {
  // Network errors (no response received)
  if (error.request && !error.response) {
    return {
      type: 'network',
      message: 'Network connection failed. Please check your internet connection and try again.',
      details: {
        endpoint: error.config?.url || 'unknown',
        method: error.config?.method?.toUpperCase() || 'unknown',
        originalError: error,
        timestamp: Date.now()
      },
      retryable: true
    };
  }

  // Timeout errors
  if (error.code === 'ECONNABORTED') {
    return {
      type: 'network',
      message: 'The request is taking longer than expected. Please try again or contact our software team if this keeps happening.',
      details: {
        endpoint: error.config?.url || 'unknown',
        method: error.config?.method?.toUpperCase() || 'unknown',
        originalError: error,
        timestamp: Date.now()
      },
      retryable: true
    };
  }

  // HTTP response errors
  if (error.response) {
    const statusCode = error.response.status;
    const endpoint = error.config?.url || 'unknown';
    const method = error.config?.method?.toUpperCase() || 'unknown';

    // Authentication errors (401/403)
    if (statusCode === 401 || statusCode === 403) {
      return {
        type: 'auth',
        message: extractMessageFromResponse(error.response) || 'Your session expired. Please log in again to continue.',
        details: {
          statusCode,
          endpoint,
          method,
          originalError: error,
          timestamp: Date.now()
        },
        retryable: false
      };
    }

    // Client errors (400-499) - Don't show in global error modal
    // These are typically validation errors or 404s that should be handled by components
    if (statusCode >= 400 && statusCode < 500) {
      // Log to console for debugging but don't show modal
      console.log('Client error (not showing modal):', {
        statusCode,
        endpoint,
        method,
        message: extractMessageFromResponse(error.response)
      });
      return null; // Don't show in global error modal
    }

    // Server errors (500-599)
    if (statusCode >= 500 && statusCode < 600) {
      // Extract clean message or use default
      const apiMessage = extractMessageFromResponse(error.response);
      const cleanMessage = apiMessage && !apiMessage.includes('at Microsoft') && !apiMessage.includes('Exception')
        ? apiMessage
        : 'Server error occurred. Please try again or contact our software team.';

      return {
        type: 'server',
        message: cleanMessage,
        details: {
          statusCode,
          endpoint,
          method,
          originalError: error,
          timestamp: Date.now()
        },
        retryable: true
      };
    }
  }

  // Unknown errors - return null to skip global error display
  return null;
};

/**
 * Extracts user-friendly message from API error response
 * Filters out technical stack traces and exception details
 * @param {Object} response - The axios response object
 * @returns {string|null} Extracted message or null
 */
export const extractMessageFromResponse = (response) => {
  if (!response || !response.data) {
    return null;
  }

  const data = response.data;

  // Helper function to check if message is technical/stack trace
  const isTechnicalMessage = (msg) => {
    if (!msg || typeof msg !== 'string') return false;
    const technicalPatterns = [
      'at Microsoft',
      'at System',
      'Exception',
      'StackTrace',
      '.dll',
      'DbConnection',
      'SqlConnection',
      'EntityFramework',
      'TaskCompletionSource',
      'retry,',
      'userOptions'
    ];
    return technicalPatterns.some(pattern => msg.includes(pattern));
  };

  // Common API error message patterns
  if (typeof data === 'string') {
    return isTechnicalMessage(data) ? null : data;
  }

  if (data.message && !isTechnicalMessage(data.message)) {
    return data.message;
  }

  if (data.error) {
    if (typeof data.error === 'string' && !isTechnicalMessage(data.error)) {
      return data.error;
    }
    if (data.error.message && !isTechnicalMessage(data.error.message)) {
      return data.error.message;
    }
  }

  if (data.errorMessage && !isTechnicalMessage(data.errorMessage)) {
    return data.errorMessage;
  }

  if (data.msg && !isTechnicalMessage(data.msg)) {
    return data.msg;
  }

  if (data.detail && !isTechnicalMessage(data.detail)) {
    return data.detail;
  }

  if (data.details) {
    if (typeof data.details === 'string' && !isTechnicalMessage(data.details)) {
      return data.details;
    }
    if (data.details.message && !isTechnicalMessage(data.details.message)) {
      return data.details.message;
    }
  }

  return null;
};

/**
 * Determines if an error is retryable based on its type
 * @param {Object} classifiedError - The classified error object
 * @returns {boolean} True if error is retryable
 */
export const isRetryable = (classifiedError) => {
  if (!classifiedError) {
    return false;
  }

  return classifiedError.retryable === true;
};

/**
 * Gets error type from classified error
 * @param {Object} classifiedError - The classified error object
 * @returns {string} Error type
 */
export const getErrorType = (classifiedError) => {
  return classifiedError?.type || 'unknown';
};

/**
 * Gets user-friendly message from classified error
 * @param {Object} classifiedError - The classified error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (classifiedError) => {
  return classifiedError?.message || 'An unexpected error occurred.';
};

/**
 * Checks if error should be excluded from global error handling
 * @param {Object} error - The error object
 * @param {Array} excludedEndpoints - Array of endpoint patterns to exclude
 * @returns {boolean} True if error should be excluded
 */
export const shouldExcludeError = (error, excludedEndpoints = []) => {
  if (!error.config?.url) {
    return false;
  }

  const endpoint = error.config.url;

  return excludedEndpoints.some(pattern => {
    if (typeof pattern === 'string') {
      return endpoint.includes(pattern);
    }
    if (pattern instanceof RegExp) {
      return pattern.test(endpoint);
    }
    return false;
  });
};
